import React, { useMemo, useCallback } from 'react';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import {
  Tabs,
  TabsSkin,
  TabsAlignment,
  Dropdown,
  DropdownTheme,
  Button,
} from 'wix-ui-tpa/cssVars';
import {
  Cart,
  EcomCart,
  Stores,
  ShoppingBag,
} from '@wix/wix-ui-icons-common/on-stage';

import { FREE_TIER_BANNER_HEIGHT_PX } from '../../../../utils/constants';
import { useGetLayoutSize } from '../../../../components-shared/LayoutSizeProvider';
import { LayoutSize } from '../../../../components-shared/LayoutSizeProvider/types';
import { viewCartStylesParams } from '../../Settings/Tabs/Design/OrderButton/stylesParams';
import {
  DROPDOWN_STYLE,
  navigationStylesParams,
} from '../../Settings/Tabs/Design/Navigation/stylesParams';

import { classes, st } from './Navigation.st.css';
import { navigationDataHooks } from './constants';
import { useHooks } from './useHooks';

type NavigationProps = {
  className?: string;
};

const Navigation: React.FC<NavigationProps> = ({ className }) => {
  const { layoutSize } = useGetLayoutSize();
  const {
    hasMultipleMenus,
    menuOptions,
    activeMenuId,
    onMenuChange,
    sectionTabs,
    activeTabId,
    onSectionTabClick,
    cartCount,
    openCart,
    isPremium,
  } = useHooks();

  const { t } = useTranslation();
  const { isEditor } = useEnvironment();
  const { get } = useStyles();
  const showIcon = get(viewCartStylesParams.viewCartShowIcon);
  const iconType = get(viewCartStylesParams.viewCartIconType);
  const ICONS = [Cart, EcomCart, Stores, ShoppingBag] as const;
  const SelectedIcon = ICONS[iconType] ?? Cart;
  const isMobile = layoutSize === LayoutSize.XS;
  const dropdownTheme =
    get(navigationStylesParams.multiMenuDropdownStyle) ===
    DROPDOWN_STYLE.UNDERLINE
      ? DropdownTheme.Line
      : DropdownTheme.Box;
  const cartButtonTitle = t('navigation.cart-button.title', {
    count: cartCount,
  });
  const cartButtonLabel = t('navigation.cart-button.title')
    .replace(/\s*\(?\{count\}\)?\s*/g, ' ')
    .trim();

  const tabItems = useMemo(
    () => sectionTabs.map((tab) => ({ title: tab.label, id: tab.id })),
    [sectionTabs],
  );

  const activeTabIndex = useMemo(
    () =>
      Math.max(
        0,
        sectionTabs.findIndex((tab) => tab.id === activeTabId),
      ),
    [sectionTabs, activeTabId],
  );

  const dropdownOptions = useMemo(
    () =>
      menuOptions.map((option) => ({
        id: option.id,
        value: option.value,
        isSelectable: true,
      })),
    [menuOptions],
  );

  const handleDropdownChange = useCallback(
    (selectedOption: { id?: string }) => {
      if (selectedOption.id) {
        onMenuChange(selectedOption.id);
      }
    },
    [onMenuChange],
  );

  if (sectionTabs.length === 0 && !hasMultipleMenus) {
    return null;
  }

  return (
    <div
      className={st(classes.root, { [layoutSize]: true }, className)}
      data-hook={navigationDataHooks.root()}
      style={{
        top: isPremium ? 0 : FREE_TIER_BANNER_HEIGHT_PX,
        ...(isEditor && { position: 'static' }),
      }}
    >
      <div className={classes.navigationBar}>
        {hasMultipleMenus && (
          <>
            <Dropdown
              className={classes.menuDropdown}
              data-hook={navigationDataHooks.menuDropdown()}
              options={dropdownOptions}
              selectedId={activeMenuId}
              onChange={handleDropdownChange}
              theme={dropdownTheme}
              upgrade
            />
            {!isMobile && (
              <div className={classes.separator} role="separator" />
            )}
          </>
        )}
        {sectionTabs.length > 0 && (
          <Tabs
            className={classes.tabs}
            items={tabItems}
            activeTabIndex={activeTabIndex}
            onTabClick={(index) => onSectionTabClick(sectionTabs[index].id)}
            skin={TabsSkin.clear}
            alignment={TabsAlignment.left}
            isTablist
          />
        )}
        {cartCount > 0 && (
          <div className={classes.viewCartBar}>
            <Button
              aria-label={cartButtonTitle}
              className={classes.viewCartButton}
              contentClassName={classes.viewCartButtonContent}
              onClick={openCart}
            >
              {showIcon && (
                <div className={classes.viewCartIcon}>
                  <SelectedIcon />
                </div>
              )}
              <span className={classes.viewCartButtonLabel}>
                {cartButtonLabel}
              </span>
              <bdi className={classes.viewCartButtonCount}>({cartCount})</bdi>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Navigation;
